import { termLabels } from "src/config/termConfig";
import { CloseSvg } from "src/assets/svg";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useEffect, useState, useMemo } from "react";
import { QuizSolveTypes } from "src/static/interface";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Drawer,
  Form,
  Modal,
  message,
} from "antd";
import {
  FinishQuizConfig,
  GetUserQuizesConfig,
  SendQuizAnswerConfig,
} from "src/service/api";
import Quiz from "src/components/quiz";
import { useUserStore } from "src/store/userStore";
import { useTranslation } from "src/i18n/context";

function QuizesSolvePage({ location = "quiz" }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const mainId = searchParams.get("id");
  const universityCodeFromParams = searchParams.get("universityCode");
  const { userInfo } = useUserStore();
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState<QuizSolveTypes[]>([]);
  const [open, setOpen] = useState(false);
  const [time, setTime] = useState<number | undefined>();
  const [endTime, setEndTime] = useState("");
  const [duration, setDuration] = useState<number>(36000);
  
  const universityCode = useMemo(() => {
    if (universityCodeFromParams) {
      return universityCodeFromParams;
    }
    
    if (!userInfo) return null;
    
    if (userInfo.universitiesInfo && userInfo.universitiesInfo.length > 0) {
      return userInfo.universitiesInfo[0].universityCode;
    }
    
    return null;
  }, [userInfo, universityCodeFromParams]);

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const handleAnswer = (quizId: number, index: number, type: string) => {
    let arr = data;
    let answerObj = arr.find((el) => el.id === quizId);
    let answers = {};
    if (answerObj) {
      if (type === "input") {
        let value = form.getFieldValue(quizId);
        if (value) {
          let map = new Map();
          map.set(index, value);
          answers = Object.fromEntries(map.entries());

          sendAnswers(quizId, answers);

          answerObj.answers = answers;
          setData(arr);
        }
      } else {
        let { variants } = answerObj;
        const getVariantValue = (v: any) => {
          if (typeof v === "string") return v;
          return v?.textUz || v?.textRu || v?.textKk || v?.textEn || "";
        };
        if (answerObj.buttonType === "radio") {
          let map = new Map();
          map.set(index, getVariantValue((variants as any)[index]));
          answers = Object.fromEntries(map.entries());
          sendAnswers(quizId, answers);

          answerObj.answers = answers;
          setData(arr);
        } else {
          let map = new Map<any, any>(Object.entries(answerObj.answers));
          if (map.get(String(index))) {
            if (Object.keys(answerObj.answers).length === 1)
              message.warning(
                "Siz eng kamida bitta javob belgilashingiz kerak"
              );
            else {
              map.delete(String(index));
              answers = Object.fromEntries(map.entries());
              sendAnswers(quizId, answers);

              answerObj.answers = answers;
              setData(arr);
            }
          } else {
            map.set(index, getVariantValue((variants as any)[index]));
            answers = Object.fromEntries(map.entries());
            sendAnswers(quizId, answers);

            answerObj.answers = answers;
            setData(arr);
          }
        }
      }
    }
  };

  const sendAnswers = (quizId: number, answers: any) => {
    let obj = {
      id: quizId,
      answers: answers,
    };
    SendQuizAnswerConfig(obj).catch((err) => {
      if (err.response.data.message === "Muddati o'tib ketgan")
        navigate("/profile/quizes");
    });
  };

  const handleFinishTest = async () => {
    if (mainId && universityCode) {
      const inputQuestions = data.filter((el) => el.buttonType === "input");
      
      if (inputQuestions.length > 0) {
        for (const question of inputQuestions) {
          const value = form.getFieldValue(question.id);
          if (!value || value.trim().length < 5) {
            message.warning("Iltimos barcha matn maydonlarini to'ldiring (kamida 5 ta belgi)!");
            const element = document.getElementById("quiz" + (data.findIndex((el) => el.id === question.id) + 1));
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            setVisible(false);
            return;
          }
        }

        try {
          await form.validateFields();
          
          for (const question of inputQuestions) {
            const value = form.getFieldValue(question.id);
            let map = new Map();
            map.set(-1, value);
            const answers = Object.fromEntries(map.entries());
            
            const questionIndex = data.findIndex((el) => el.id === question.id);
            if (questionIndex !== -1) {
              data[questionIndex].answers = answers;
            }
            
            await sendAnswers(question.id, answers);
          }
        } catch (error) {
          message.warning("Iltimos barcha matn maydonlarini to'ldiring!");
          setVisible(false);
          return;
        }
      }

      // Boshqa savollarni tekshirish
      let unfinished = data.find((el) => Object.keys(el.answers).length === 0);
      if (unfinished) {
        message.warning(termLabels.completeWarning);
      } else {
        FinishQuizConfig(mainId, universityCode).then(() => {
          message.success(termLabels.answersSaved);
          navigate(`/profile/quizes?status=Yakunlangan`);
        });
      }
    }
  };

  const makeAnswersCount = () => {
    let count = 0;
    data.forEach((item) => {
      if (Object.keys(item.answers).length > 0) count++;
    });
    return count;
  };

  const getFunc = async () => {
    if (mainId && universityCode) {
      GetUserQuizesConfig(+mainId, universityCode).then((res) => {
        let arr = res.data;
        setData(arr);

        let map = new Map();
        arr.forEach((item: QuizSolveTypes) => {
          if (item.buttonType === "input" && item.answers[-1])
            map.set(item.id, item.answers[-1]);
        });

        let answers = Object.fromEntries(map.entries());
        form.setFieldsValue(answers);

        if (arr.length > 0) {
          setEndTime(arr[0].endTime);
          setDuration((arr[0].endTime - arr[0].startTime) / 100);
        }
      }).catch((err) => {
        message.error("Ma'lumotlarni yuklashda xatolik yuz berdi");
        console.error(err);
      });
    }
  };

  useEffect(() => {
    getFunc();
  }, [universityCode]);

  const resultsContent = (
    <div className="test-table-numbers">
      {data.map((item, index) => {
        return (
          <a
            href={`#quiz${index + 1}`}
            className={`${
              Object.keys(item.answers).length > 0 && "test-table-number"
            }`}
            onClick={onClose}
            key={index + "numbers"}
          >
            {index + 1}
          </a>
        );
      })}
    </div>
  );

  const tableContent = (
    <div className="test-table-content">
      {open && (
        <div className="test-table-close">
          <span onClick={onClose}>
            <CloseSvg />
          </span>
        </div>
      )}

      <div className="test-table-header">
        <h2>{termLabels.single}</h2>
        <div className="test-table-header-info">
          <p>
            <span>{makeAnswersCount()}</span>/{data.length}
          </p>
          <div className="test-table-header-info-right">
            <span className="time">
              {/* <span style={{ marginBottom: "-6px" }}>Vaqt:</span>{" "}
              <Statistic.Countdown
                onChange={(val) =>
                  typeof val === "number" && setTime(val / duration)
                }
                format="DD:HH:mm:ss"
                value={endTime}
              /> */}
            </span>
            {/* <Progress percent={time} size="small" showInfo={false} /> */}
          </div>
        </div>
      </div>
      {resultsContent}
      {/* <a
        href="tel:+998935976655"
        className="quiz-feedback-phone"
      >
        {t("quizzes.feedbackPhone")}
      </a> */}
      <div onClick={() => setVisible(true)} className="test-table-footer">
        Yakunlash
      </div>
    </div>
  );

  return (
    <>
      {location === "quiz" ? (
        <>
          <div className="test">
            <div className="test-content container">
              <Button
                onClick={showDrawer}
                className="test-button"
                type="primary"
                size="large"
              >
                Batafsil
              </Button>
              <div className="test-container">
                <h2 style={{ marginBottom: "20px" }}>{data[0]?.theme}</h2>
                <Form form={form} layout="vertical">
                  {data.map((item, index) => (
                    <Quiz
                      data={item}
                      index={index}
                      location={location}
                      key={index + "key"}
                      handleAnswer={handleAnswer}
                    />
                  ))}
                </Form>
              {/* <a
                href="tel:+998935976655"
                className="quiz-feedback-phone"
              >
                {t("quizzes.feedbackPhone")}
              </a> */}
                <div
                  onClick={() => setVisible(true)}
                  className="test-table-footer test-container-footer"
                >
                  Yakunlash
                </div>
              </div>
            </div>
            <div className="test-table">{tableContent}</div>
            <Drawer
              className="test-drawer"
              width="auto"
              title=""
              placement="right"
              onClose={onClose}
              open={open}
            >
              {tableContent}
            </Drawer>

            {/* Finish test modal */}
            <Modal
              title=""
              width={350}
              open={visible}
              okText="Ha"
              cancelText="Bekor qilish"
              onOk={handleFinishTest}
              onCancel={() => setVisible(false)}
              className="no-close-button"
            >
              <h2 style={{ textAlign: "center" }} className="no-data-p">
                {termLabels.finishConfirm}
              </h2>
              {resultsContent}
              <br />
            </Modal>
          </div>{" "}
        </>
      ) : (
        <>
          <Button
            icon={<ArrowLeftOutlined />}
            style={{ marginBottom: 16 }}
            onClick={() => navigate("/profile/quizes?status=Yakunlangan")}
          >
            Orqaga
          </Button>
          <div className="test profile__test" style={{ borderRadius: "10px" }}>
            <div
              className="test-container"
              style={{ padding: "20px", borderRadius: "10px" }}
            >
              <Form form={form} layout="vertical">
                {data.map((item: any, index: number) => (
                  <Quiz
                    data={item}
                    index={index}
                    location={location}
                    key={index + "key"}
                    handleAnswer={() => {}}
                  />
                ))}
              </Form>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default QuizesSolvePage;
